import { useEffect } from 'react';
import { createPortal } from 'react-dom';

// A large CENTERED dialog for editing a record: header, scrollable body, and a
// pinned footer that keeps the save action in view however long the content is.
//
// Sibling of Modal, not a replacement — Modal is for a short confirmation that
// sizes to its text, this is for a full record whose body has to scroll on its
// own while the actions stay put.
//
// Rendered through a portal to <body> so its `fixed inset-0` overlay is measured
// against the viewport rather than the AppShell subtree.
const SIZE_CLS = { lg: 'max-w-lg', xl: 'max-w-xl', '2xl': 'max-w-2xl', '3xl': 'max-w-3xl', '4xl': 'max-w-4xl' };

export function DetailModal({ open, onClose, title, subtitle, badge, children, footer, size = '3xl' }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // The page behind must not scroll under the dialog on a trackpad flick.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : 'Details'}
        // max-h + flex column is what lets the body scroll while header and
        // footer stay pinned.
        className={`relative z-10 flex max-h-[90vh] w-full ${SIZE_CLS[size] || SIZE_CLS['3xl']} flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-white/10`}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-6 py-4 dark:border-white/10">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <h3 className="truncate text-lg font-semibold text-navy-800 dark:text-slate-100">{title}</h3>
              {badge}
            </div>
            {subtitle && <p className="mt-0.5 truncate text-xs text-slate-400 dark:text-slate-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:text-slate-500 dark:hover:bg-white/10"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer && (
          <div className="shrink-0 border-t border-slate-200 bg-slate-50/80 px-6 py-3.5 dark:border-white/10 dark:bg-white/[0.03]">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
